/**
 * Personal Activity Report
 *
 * Top fixed section inside Master Audit. Visible ONLY for admins where
 * admin_users.personal_activity_report_enabled = true (server-enforced).
 *
 * Shows a rolling 7-day window of the calling admin's own:
 *   - high-value KYC / payment / commission / withdrawal / user-edit actions
 *   - searches (surface + query)
 *   - tab navigation
 *
 * Export: CSV, XLSX, PDF (client-side, no server cost). Touches no audit
 * or financial ledgers — driven solely by admin_personal_activity_log.
 */

import _React, { useCallback, useEffect, useMemo, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import { isPersonalActivityEnabled } from '../../lib/personalActivityLogger'
import {
  exportPersonalActivityCsv,
  exportPersonalActivityXls,
  exportPersonalActivityPdf,
  type PersonalActivityRow
} from '../../lib/personalActivityExport'
import { PersonalActivityHeader, PersonalActivityBody } from './PersonalActivityReport.parts'

interface Props { currentUser?: { email?: string } | null }

function isoDayStart(d: Date): string {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x.toISOString()
}
function isoDayEnd(d: Date): string {
  const x = new Date(d)
  x.setHours(23, 59, 59, 999)
  return x.toISOString()
}
function todayStr(): string { return new Date().toISOString().slice(0, 10) }
function daysAgoStr(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

export function PersonalActivityReport({ currentUser }: Props) {
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [rows, setRows] = useState<PersonalActivityRow[]>([])
  const [total, setTotal] = useState<number>(0)
  const [from, setFrom] = useState<string>(daysAgoStr(7))
  const [to, setTo] = useState<string>(todayStr())
  const [category, setCategory] = useState<string>('')
  const [q, setQ] = useState<string>('')

  useEffect(() => {
    let cancelled = false
    isPersonalActivityEnabled().then(v => { if (!cancelled) setEnabled(v) })
    return () => { cancelled = true }
  }, [])

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams()
      if (from) params.set('from', isoDayStart(new Date(from)))
      if (to) params.set('to', isoDayEnd(new Date(to)))
      if (category) params.set('category', category)
      if (q) params.set('q', q)
      params.set('limit', '1000')
      const res = await fetch(`/api/admin/personal-activity/list?${params.toString()}`, {
        method: 'GET',
        headers: { ...getSessionHeaders() }
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json?.success === false) {
        setError(json?.error || `Request failed (${res.status})`)
        setRows([]); setTotal(0)
        return
      }
      setRows(json.rows || [])
      setTotal(json.total || 0)
    } catch (e: any) {
      setError(e?.message || 'Network error')
    } finally {
      setLoading(false)
    }
  }, [from, to, category, q])

  useEffect(() => {
    if (enabled) load()
  }, [enabled, load])

  const meta = useMemo(() => ({
    from: from ? isoDayStart(new Date(from)) : undefined,
    to: to ? isoDayEnd(new Date(to)) : undefined
  }), [from, to])

  const adminEmail = currentUser?.email || undefined

  if (enabled === null) return null
  if (!enabled) return null

  return (
    <div
      data-personal-activity-report
      style={{
        marginBottom: '24px',
        backgroundColor: '#1F2937',
        border: '1px solid #374151',
        borderRadius: '12px',
        padding: '16px 20px'
      }}
    >
      <PersonalActivityHeader
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        rowCount={rows.length}
        total={total}
        loading={loading}
        onRefresh={load}
        onExportCsv={() => exportPersonalActivityCsv(rows, adminEmail)}
        onExportXls={() => exportPersonalActivityXls(rows, adminEmail)}
        onExportPdf={() => exportPersonalActivityPdf(rows, adminEmail, meta)}
      />

      {!collapsed && (
        <PersonalActivityBody
          from={from} to={to} category={category} q={q}
          setFrom={setFrom} setTo={setTo} setCategory={setCategory} setQ={setQ}
          rows={rows} loading={loading} error={error}
        />
      )}
    </div>
  )
}

export default PersonalActivityReport
